import {
  ADJACENT_OOS_ITEM_FOUND,
  UPDATE_ORDER_ITEM_QUANTITY,
  UPDATE_ORDER_ITEM_STATUS,
} from '../../../src/config/graphQL/documentNode/mutation';
import {
  AOOSData,
  AdjacentOOSItemFoundArgs,
  Buyers,
  GetBuyersForItemsResponse,
  GetItemsToLocateArgs,
  GetItemsToLocateResponse,
  GetRunForItemDetailResponse,
  HandleItemFoundResponse,
  RunForItemDetail,
  State,
  UpdateOrderItemQtyVariables,
  UpdateOrderItemQuantityResponse,
} from '../types/SubstituteItemDetails';
import {
  APIV1Version,
  ApiState,
  OrderItemStatus,
} from '../../../src/common/types/common';
import {Dispatch, useCallback, useContext, useReducer, useState} from 'react';
import {
  GET_ADJACENT_OOS_ITEM,
  GET_BUYERS_FOR_ITEM,
  GET_RUN_FOR_ITEM_DETAIL,
} from '../../config/graphQL/documentNode/queries';
import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {
  SubstituteItemDetailsContext,
  initialState,
} from '../context/SubstituteItemDetails';

import {OrderItem} from '@buncha/home/types';
import {OrderItem as OrderItemTypeScanner} from '../types/ItemScanner';
import {ToastType} from '../../components/composites/notification/type';
import {gqlService} from '../../config/graphQL';
import {useGetConstantsForApp} from '@buncha/common/hooks/common';
import {useToastMessage} from './../../components/composites/notification/NativeBaseToast';

const reducer = {
  setItemData: function (
    state: State,
    action: PayloadAction<OrderItemTypeScanner>,
  ) {
    state.item = action.payload;
  },
  setBuyersForItem: function (state: State, action: PayloadAction<Buyers[]>) {
    state.buyersForItem = action.payload;
  },
  setReasonForDrop: function (state: State, action: PayloadAction<any>) {
    state.reasonsForDropping = action.payload;
  },
  hasAnyCustomerInstructionMsg: function (
    state: State,
    action: PayloadAction<boolean>,
  ) {
    state.hasAnyBuyerInstructions = action.payload;
  },
  setOrderItemIds: function (state: State, action: PayloadAction<number[]>) {
    state.orderItemIds = action.payload;
  },
  setConstants: function (state: State, action: PayloadAction<any>) {
    state.constants = action.payload;
  },
  setFountAt: function (state: State, action: PayloadAction<Date>) {
    const date = new Date(action.payload);
    const minutes = parseInt(state?.constants?.WAIT_TIME_FOR_AOOS, 10) ?? 10;
    const dateWithOffset = new Date(date.getTime() + minutes * 60 * 1000);
    state.foundAt = dateWithOffset;
  },
  setItemStatus: function (state: State, action: PayloadAction<string>) {
    state.itemStatus = action.payload;
  },
  setRunData: function (state: State, action: PayloadAction<RunForItemDetail>) {
    state.runData = action.payload;
  },
  setRunDataLoading: function (state: State, action: PayloadAction<boolean>) {
    state.runDataLoading = action.payload;
  },
  setRunId: function (state: State, action: PayloadAction<number>) {
    state.runId = action.payload;
  },
  setNewOrderId: function (state: State, action: PayloadAction<number>) {
    state.newOrderId = action.payload;
  },
  updateQtyByOrderItemId: function (
    state: State,
    action: PayloadAction<{updatedQty: number; orderItemId: number}>,
  ) {
    const {orderItemId, updatedQty} = action.payload;
    const data = state.buyersForItem.find(
      order => order?.orderItemId === orderItemId,
    );
    if (data) data.quantity = updatedQty;
  },
  setItemDataWithUpdatedQuantity: function (
    state: State,
    action: PayloadAction<{totalQty: number}>,
  ) {
    const {totalQty} = action.payload;
    if (state.item) state.item.totalupdatedQuantityByShopper = totalQty;
  },
  setAOOSData: function (state: State, action: PayloadAction<AOOSData>) {
    state.aoosData = action.payload;
  },
};

const slice = createSlice({
  initialState,
  name: 'SubstituteItemDetailsContext',
  reducers: reducer,
});

export function useSubstituteItemDetailsReducer() {
  const reducerInfo = useReducer(slice.reducer, initialState);
  return reducerInfo;
}

export function useSubstituteItemDetailsContext() {
  return useContext(SubstituteItemDetailsContext);
}

export const SubstituteItemDetailsAction = slice.actions;

export function useBuyerManagement(dispatch: Dispatch<any> | null) {
  const [loadingBuyer, setLoadingBuyer] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const getBuyersForItem = async (
    runId: number,
    orderItemId: number,
    catalogItemId: number,
  ) => {
    try {
      setLoadingBuyer(true);
      const res = await gqlService?.query<GetBuyersForItemsResponse>({
        query: GET_BUYERS_FOR_ITEM,
        fetchPolicy: 'network-only',
        variables: {
          orderItemId: orderItemId,
          runId,
          catalogItemId,
        },
      });
      if (res?.data && dispatch) {
        const val = res?.data?.itemWithBuyersAndSub;
        const orderItemsIds: number[] = [];

        val?.forEach(item => orderItemsIds.push(item?.orderItemId || 0));
        dispatch(SubstituteItemDetailsAction.setBuyersForItem(val));
        const hasAnyInstuction = val.find(i => Boolean(i.substitutionNote));
        dispatch(
          SubstituteItemDetailsAction.hasAnyCustomerInstructionMsg(
            Boolean(hasAnyInstuction),
          ),
        );
        dispatch(SubstituteItemDetailsAction.setOrderItemIds(orderItemsIds));
      }
    } catch (error: any) {
      showErrorToast(error.message);
    } finally {
      setLoadingBuyer(false);
    }
  };

  return {loadingBuyer, getBuyersForItem};
}

export function useConfig(dispatch?: Dispatch<any> | null) {
  const [loading, setLoading] = useState(true);
  const [reasonList, setReasonList] = useState();
  const [subRequestLife, setSubRequestLife] = useState(0);
  const {getConstantsForApp} = useGetConstantsForApp();
  const getConstants = async () => {
    setLoading(true);
    try {
      const data = await getConstantsForApp();

      const dropReaons = JSON.parse(data?.REASON_FOR_DROPPING);
      const reqLife = Number(data?.SUBSTITUTION_REQUEST_LIFE);
      setSubRequestLife(reqLife);
      const configData = data?.CONSTANTS?.RETRY_CONFIG;
      const loggingData = data?.CONSTANTS?.LOGGING_DATA;
      if (configData) gqlService?.setRetryConfigData(configData);
      if (loggingData) gqlService?.setLoggingDelayData(loggingData);
      if (dispatch)
        dispatch(SubstituteItemDetailsAction.setReasonForDrop(dropReaons));
      setReasonList(dropReaons);

      setLoading(false);
    } catch (error: any) {
      setLoading(false);
    }
  };

  return {getConstants, loading, reasonList, subRequestLife};
}

export function useDropOrderItem() {
  const [droppingItem, setDroppingItem] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const dropOrderItem = async (orderItemIds: number[]) => {
    setDroppingItem(true);
    try {
      await gqlService?.mutation({
        mutation: UPDATE_ORDER_ITEM_STATUS,
        variables: {
          orderItemIds: orderItemIds,
          orderItemStatus: OrderItemStatus.dropped,
        },
      });
    } catch (err: any) {
      showErrorToast(err?.message);
    } finally {
      setDroppingItem(false);
    }
  };
  return {dropOrderItem, droppingItem};
}

export function useGetRunForItemDetail(
  dispatch: Dispatch<any> | null,
): [(runId: number) => void, ApiState<RunForItemDetail>] {
  const [state, setState] = useState<ApiState<RunForItemDetail>>({
    loading: false,
    error: undefined,
    data: undefined,
  });

  const getRunForItemDetail = useCallback(
    async (runId: number) => {
      if (dispatch)
        dispatch(SubstituteItemDetailsAction.setRunDataLoading(true));
      setState({...state, loading: true});
      try {
        const response = await gqlService?.query<GetRunForItemDetailResponse>({
          query: GET_RUN_FOR_ITEM_DETAIL,
          variables: {
            runId: runId,
          },
          fetchPolicy: 'cache-first',
        });
        if (response?.data?.getRun)
          setState({...state, data: response?.data.getRun, loading: false});
        if (dispatch && response?.data?.getRun) {
          dispatch(
            SubstituteItemDetailsAction.setRunData(response?.data?.getRun),
          );
          dispatch(SubstituteItemDetailsAction.setRunDataLoading(false));
        }
      } catch (error: any) {
        //use toast
        if (dispatch)
          dispatch(SubstituteItemDetailsAction.setRunDataLoading(false));
        setState({...state, loading: false});
      }
    },
    [dispatch, state],
  );

  return [getRunForItemDetail, state];
}

export const useGetConstants = (dispatch: Dispatch<any> | null) => {
  const [constantsLoading, setConstantsLoading] = useState(false);
  const {getConstantsForApp} = useGetConstantsForApp();
  const getConstants = useCallback(async () => {
    setConstantsLoading(true);
    try {
      const data = await getConstantsForApp();
      if (data && dispatch)
        dispatch(SubstituteItemDetailsAction.setConstants(data));
      const configData = data?.RETRY_CONFIG;
      if (configData) gqlService?.setRetryConfigData(configData);
    } catch (error: any) {
    } finally {
      setConstantsLoading(false);
    }
  }, [dispatch, getConstantsForApp]);

  return {getConstants, constantsLoading};
};

export function useUpdateOrderItemQuantity() {
  const [info, setInfo] = useState<ApiState<OrderItem>>({
    data: null,
    error: undefined,
    loading: false,
  });
  const updateOrderItemQuantity = useCallback(
    async (params: UpdateOrderItemQtyVariables, callback: Function) => {
      setInfo({
        ...info,
        loading: true,
      });
      try {
        const res = await gqlService?.mutation<UpdateOrderItemQuantityResponse>(
          {
            mutation: UPDATE_ORDER_ITEM_QUANTITY,
            fetchPolicy: 'network-only',
            variables: params,
          },
        );
        if (res?.data)
          setInfo({
            ...info,
            data: res?.data?.updateOrderItemQuantity,
            loading: false,
          });
        callback(res?.data?.updateOrderItemQuantity);
      } catch (error: any) {
        setInfo({
          ...info,
          error: new Error(error),
        });
      }
    },
    [info],
  );

  return {updateOrderItemQuantity, info};
}

export function useAdjacentOOSItemFound() {
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const adjacentOOSItemFound = async (
    version: APIV1Version,
    reqParams: AdjacentOOSItemFoundArgs,
  ) => {
    try {
      const res = await gqlService?.mutation<HandleItemFoundResponse>({
        mutation: ADJACENT_OOS_ITEM_FOUND,
        variables: {
          requestParams: reqParams,
          version: APIV1Version.V100,
        },
        fetchPolicy: 'network-only',
        context: {
          headers: {
            'graphql-version': 'v1',
          },
        },
      });
      return res?.data;
    } catch (error: any) {
      showErrorToast(error?.message);
    }
  };

  return {adjacentOOSItemFound};
}

export function useGetAdjacentOOSItem(dispatch: Dispatch<any> | null) {
  const getAdjacentOOSItem = async (
    version: APIV1Version,
    reqParams: GetItemsToLocateArgs,
  ) => {
    try {
      const res = await gqlService?.mutation<GetItemsToLocateResponse>({
        mutation: GET_ADJACENT_OOS_ITEM,
        variables: {
          requestParams: reqParams,
          version: APIV1Version.V100,
        },
        fetchPolicy: 'network-only',
        context: {
          headers: {
            'graphql-version': 'v1',
          },
        },
      });
      if (
        res?.data?.getItemsToLocate?.data?.V100?.adjacentOOSItems &&
        dispatch
      ) {
        const aoosData =
          res?.data?.getItemsToLocate?.data?.V100?.adjacentOOSItems?.[0]
            ?.aoosData;
        const foundAt =
          res?.data?.getItemsToLocate?.data?.V100?.adjacentOOSItems?.[0]
            ?.foundAt;
        const itemStatus =
          res?.data?.getItemsToLocate?.data?.V100?.adjacentOOSItems?.[0]
            ?.itemStatus;
        const runId =
          res?.data?.getItemsToLocate?.data?.V100?.adjacentOOSItems?.[0]?.runId;
        const newOrderId =
          res?.data?.getItemsToLocate?.data?.V100?.adjacentOOSItems?.[0]
            ?.orderId;
        dispatch(
          SubstituteItemDetailsAction.setItemData(
            res?.data?.getItemsToLocate?.data?.V100?.adjacentOOSItems[0]
              .orderItem,
          ),
        );
        if (foundAt) dispatch(SubstituteItemDetailsAction.setFountAt(foundAt));
        if (itemStatus)
          dispatch(SubstituteItemDetailsAction.setItemStatus(itemStatus));
        if (runId) dispatch(SubstituteItemDetailsAction.setRunId(runId));
        if (newOrderId)
          dispatch(SubstituteItemDetailsAction.setNewOrderId(newOrderId));
        if (aoosData)
          dispatch(SubstituteItemDetailsAction.setAOOSData(aoosData));
      }
    } catch (error) {}
  };

  return {getAdjacentOOSItem};
}
